import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { LogOut, ShieldCheck } from 'lucide-react';
import { config } from '../../config/env';
import { useToast } from '../../components/ui/Toast';
import { tenantRegistry } from '../../lib/tenantRegistry';
import {
  tenantRegistrationService,
  buildTenantUrl,
  extractApiError,
} from '../../services/tenantRegistrationService';
import type {
  RegisterTenantRequest,
  TenantListItem,
  TenantRegistryEntry,
  TenantRow,
} from '../../types/tenantRegistration';
import { RegistrarLoginForm } from './RegistrarLoginForm';
import { RegisterTenantForm } from './RegisterTenantForm';
import { TenantList } from './TenantList';
import { TenantAccessPanel } from './TenantAccessPanel';

const REGISTRAR_USER_KEY = 'C2_tenantRegistrar_user';

function mergeRows(apiTenants: TenantListItem[], local: TenantRegistryEntry[]): TenantRow[]
{
  const rows = new Map<string, TenantRow>();

  for (const tenant of apiTenants)
  {
    const key = tenant.TenancyName.toLowerCase();
    rows.set(key, {
      tenancyName: tenant.TenancyName,
      emailAddress: tenant.EmailAddress || '',
      name: tenant.Name || tenant.TenancyName,
      tenantUrl: buildTenantUrl(tenant.TenancyName),
      serverUrl: '',
      isActive: tenant.IsActive,
      createdAt: tenant.CreationTime || null,
      registeredUsers: tenant.RegisteredUsers ?? null,
      lastActivity: tenant.LastActivity,
      lastEmail: null,
      emails: [],
      source: 'api',
    });
  }

  for (const entry of local)
  {
    const key = entry.tenancyName.toLowerCase();
    const emails = entry.emails || [];
    const lastEmail = emails.length > 0 ? emails[emails.length - 1] ?? null : null;
    const existing = rows.get(key);
    if (existing)
    {
      rows.set(key, {
        ...existing,
        emailAddress: existing.emailAddress || entry.emailAddress,
        tenantUrl: entry.tenantUrl || existing.tenantUrl,
        serverUrl: entry.serverUrl,
        createdAt: existing.createdAt || entry.createdAt || null,
        lastEmail,
        emails,
        source: 'both',
      });
    }
    else
    {
      rows.set(key, {
        tenancyName: entry.tenancyName,
        emailAddress: entry.emailAddress,
        name: entry.tenancyName,
        tenantUrl: entry.tenantUrl || buildTenantUrl(entry.tenancyName),
        serverUrl: entry.serverUrl,
        isActive: null,
        createdAt: entry.createdAt || null,
        registeredUsers: null,
        lastActivity: null,
        lastEmail,
        emails,
        source: 'local',
      });
    }
  }

  return Array.from(rows.values()).sort((a, b) =>
  {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });
}

/**
 * Tenant registration console. Alternate URL enabled through
 * VITE_TENANT_REGISTRATION_ENABLED, protected by the fixed registrar account.
 */
export const TenantRegistrationPage: React.FC = () =>
{
  const toast = useToast();
  const [registrarUser, setRegistrarUser] = useState<string | null>(() =>
    sessionStorage.getItem(REGISTRAR_USER_KEY),
  );
  const [hasApiSession, setHasApiSession] = useState<boolean>(
    () => Boolean(tenantRegistrationService.getRegistrarToken()),
  );
  const [apiTenants, setApiTenants] = useState<TenantListItem[]>([]);
  const [localEntries, setLocalEntries] = useState<TenantRegistryEntry[]>(() =>
    tenantRegistry.getAll(),
  );
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState('');
  const [selected, setSelected] = useState<TenantRow | null>(null);
  const [resendingTenant, setResendingTenant] = useState<string | null>(null);

  const refreshLocal = useCallback(() =>
  {
    setLocalEntries(tenantRegistry.getAll());
  }, []);

  const loadTenants = useCallback(async () =>
  {
    refreshLocal();
    if (!tenantRegistrationService.getRegistrarToken())
    {
      setHasApiSession(false);
      return;
    }
    setLoadingList(true);
    setListError('');
    try
    {
      const tenants = await tenantRegistrationService.getTenants(false);
      setApiTenants(tenants);
      setHasApiSession(true);
    }
    catch (error)
    {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403)
      {
        setListError(
          'The registrar account has no permission to list tenants on the server (Pages.Tenants). Showing local data only.',
        );
      }
      else
      {
        setListError(extractApiError(error, 'Could not load the tenant list from the server.'));
      }
    }
    finally
    {
      setLoadingList(false);
    }
  }, [refreshLocal]);

  useEffect(() =>
  {
    if (registrarUser)
    {
      loadTenants();
    }
  }, [registrarUser, loadTenants]);

  const rows = useMemo(() => mergeRows(apiTenants, localEntries), [apiTenants, localEntries]);

  // Keep the side panel in sync with the latest data of the selected tenant
  useEffect(() =>
  {
    if (!selected) return;
    const fresh = rows.find(
      row => row.tenancyName.toLowerCase() === selected.tenancyName.toLowerCase(),
    );
    if (fresh && fresh !== selected)
    {
      setSelected(fresh);
    }
  }, [rows, selected]);

  const handleLoginSuccess = useCallback((user: string, apiSession: boolean) =>
  {
    sessionStorage.setItem(REGISTRAR_USER_KEY, user);
    setRegistrarUser(user);
    setHasApiSession(apiSession);
  }, []);

  const handleLogout = useCallback(() =>
  {
    sessionStorage.removeItem(REGISTRAR_USER_KEY);
    tenantRegistrationService.clearRegistrarToken();
    setRegistrarUser(null);
    setHasApiSession(false);
    setApiTenants([]);
    setSelected(null);
  }, []);

  const handleRegistered = useCallback(
    (request: RegisterTenantRequest, tenantUrl: string) =>
    {
      const now = new Date().toISOString();
      tenantRegistry.upsert({
        tenancyName: request.TenancyName,
        emailAddress: request.EmailAddress,
        firstName: request.FirstName,
        lastName: request.LastName,
        tenantUrl,
        serverUrl: '',
        createdAt: now,
        createdBy: registrarUser || '',
        // The API sends the welcome email as part of RegisterTenant
        emails: [{ kind: 'welcome', sentTo: request.EmailAddress, sentAt: now, ok: true }],
      });
      toast.success(`Welcome email sent to ${request.EmailAddress}`, 'Tenant created');
      loadTenants();
    },
    [registrarUser, toast, loadTenants],
  );

  const resendFor = useCallback(
    async (row: TenantRow, serverUrl: string) =>
    {
      tenantRegistry.ensure(row.tenancyName, row.emailAddress, row.tenantUrl);
      try
      {
        await tenantRegistrationService.resendAccessEmail(
          serverUrl,
          row.tenancyName,
          row.emailAddress,
        );
        tenantRegistry.addEmail(row.tenancyName, {
          kind: 'access-reset',
          sentTo: row.emailAddress,
          sentAt: new Date().toISOString(),
          ok: true,
        });
        refreshLocal();
      }
      catch (error)
      {
        tenantRegistry.addEmail(row.tenancyName, {
          kind: 'access-reset',
          sentTo: row.emailAddress,
          sentAt: new Date().toISOString(),
          ok: false,
          error: extractApiError(error, 'Unknown error'),
        });
        refreshLocal();
        throw error;
      }
    },
    [refreshLocal],
  );

  const handleResendFromList = useCallback(
    async (row: TenantRow) =>
    {
      setResendingTenant(row.tenancyName);
      try
      {
        await resendFor(row, row.serverUrl);
        toast.success(`Access email sent to ${row.emailAddress}`, row.tenancyName);
      }
      catch (error)
      {
        toast.error(extractApiError(error, 'The email could not be sent.'), row.tenancyName);
      }
      finally
      {
        setResendingTenant(null);
      }
    },
    [resendFor, toast],
  );

  const handleSaveServer = useCallback(
    (tenancyName: string, serverUrl: string) =>
    {
      const row = rows.find(item => item.tenancyName === tenancyName);
      tenantRegistry.ensure(tenancyName, row?.emailAddress || '', row?.tenantUrl || '');
      tenantRegistry.setServerUrl(tenancyName, serverUrl);
      refreshLocal();
      toast.success('Server saved for this tenant', tenancyName);
    },
    [rows, refreshLocal, toast],
  );

  if (!config.tenantRegistration.enabled)
  {
    return <Navigate to="/" replace />;
  }

  if (!registrarUser)
  {
    return <RegistrarLoginForm onSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="min-h-screen bg-[#ededed]">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-[#62accb]" />
            <div>
              <h1 className="text-lg font-medium text-gray-800 leading-tight">
                Tenant Registration
              </h1>
              <p className="text-xs text-gray-500">
                Signed in as {registrarUser}
                {hasApiSession ? '' : ' · no server session for the tenant list'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-800"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 py-6 grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)] gap-6">
        <div>
          <RegisterTenantForm onRegistered={handleRegistered} />
        </div>
        <div className="min-w-0">
          <TenantList
            rows={rows}
            loading={loadingList}
            hasApiSession={hasApiSession}
            apiError={listError}
            onRefresh={loadTenants}
            onOpenEmail={setSelected}
            onResend={handleResendFromList}
            resendingTenant={resendingTenant}
          />
        </div>
      </main>

      {selected && (
        <TenantAccessPanel
          row={selected}
          onClose={() => setSelected(null)}
          onSaveServer={handleSaveServer}
          onResend={resendFor}
        />
      )}
    </div>
  );
};

export default TenantRegistrationPage;
